import { useQuery } from '@tanstack/react-query';
import gql from 'graphql-tag';
import useGraphQLPaging from 'hooks/graphql/useGraphQLPaging/useGraphQLPaging';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import {
  GetAllOrganizationSignInQuestionsQuery,
  GetAllOrganizationSignInQuestionsQueryVariables,
  OrganizationSignInQuestionFilter,
  SortOrder,
} from '__generated__/graphql';
import { useFeatureFlags } from 'utils/features/hooks';

export type OrganizationSignInQuestionItem = Omit<
  NonNullable<GetAllOrganizationSignInQuestionsQuery['organizationSignInQuestions']>[number],
  'cursor'
>;

const EMPTY_QUESTIONS: OrganizationSignInQuestionItem[] = [];

export default function useGetOrganizationSignInQuestions(filter?: OrganizationSignInQuestionFilter) {
  const pageAll = useGraphQLPaging();
  const baseQueryKey = useReactQueryBaseKey();
  const isDailySignInEnabled = useFeatureFlags('DAILY_SIGN_IN_QUESTIONS');
  const query = useQuery({
    enabled: isDailySignInEnabled,
    queryKey: [...baseQueryKey, 'getAllOrganizationSignInQuestions', filter],
    queryFn: (): Promise<OrganizationSignInQuestionItem[]> => {
      return pageAll<GetAllOrganizationSignInQuestionsQuery, GetAllOrganizationSignInQuestionsQueryVariables>(
        {
          document: QUERY_ORGANIZATION_SIGN_IN_QUESTIONS,
          variables: {
            filter: { ...filter },
            sort: [{ order: SortOrder.Asc }],
          },
        },
        'organizationSignInQuestions'
      );
    },
  });

  return { ...query, data: query.data ?? EMPTY_QUESTIONS };
}

export const QUERY_ORGANIZATION_SIGN_IN_QUESTIONS = gql`
  query getAllOrganizationSignInQuestions(
    $filter: OrganizationSignInQuestionFilter
    $first: Int
    $sort: [OrganizationSignInQuestionSort!]
    $after: String
  ) {
    organizationSignInQuestions(filter: $filter, first: $first, sort: $sort, after: $after) {
      id
      audienceType
      choices
      createdOn
      cursor
      deletedOn
      organizationId
      positionId
      position {
        id
        title
      }
      questionDescription
      questionPrompt
      questionTitle
      required
      submittedOn
      type
      updatedOn
      order
    }
  }
`;
